/**
 * Where the citizen lives — asked only where a company journey needs it.
 *
 * A company's journey opens (`pending_setup` → `active`) once the platform
 * knows where the citizen is moving from and has recorded them as set up. The
 * server does both in one write (`POST /users/me/setup`, `completeSetup`): it
 * saves the country and city, stamps the citizen as set up and activates every
 * journey that was waiting. There is no setup SCREEN any more — the question is
 * asked in place, where it matters: reviewing an invitation, accepting one from
 * its email, and on a journey still waiting for it.
 *
 * `needed` is the server's answer (`setupComplete`), never guessed from the
 * profile: a citizen can have typed a city in their profile and still not be
 * set up. The form starts from what the profile already knows.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { citizenSetupSchema, type CitizenSetupInput } from '@tonnie/schemas';
import { useCompleteCitizenSetup, useOnboardingStatus, useUserProfile } from '@tonnie/sdk';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

export function useResidence() {
  const status = useOnboardingStatus();
  const profile = useUserProfile();
  const complete = useCompleteCitizenSetup();

  const form = useForm<CitizenSetupInput>({
    resolver: zodResolver(citizenSetupSchema),
    mode: 'onTouched',
    defaultValues: { currentCountry: '', currentCity: '' },
  });

  // Pre-fill what the profile already knows — once, and never over something
  // the citizen has started typing.
  const talent = profile.data?.talent ?? null;
  const prefilled = useRef(false);
  useEffect(() => {
    if (prefilled.current || !talent) return;
    prefilled.current = true;
    const { currentCountry, currentCity } = form.getValues();
    if (!currentCountry && talent.currentCountry) {
      form.setValue('currentCountry', talent.currentCountry);
    }
    if (!currentCity && talent.currentCity) form.setValue('currentCity', talent.currentCity);
  }, [talent, form]);

  return {
    /** The server has answered, and the citizen is not set up. */
    needed: status.isSuccess && status.data?.setupComplete === false,
    /** Still asking the server — an accept must wait for the answer. */
    isChecking: status.isPending,
    form,
    /**
     * Validate and save. Resolves `true` once the server has recorded it (the
     * journey caches are refreshed by the mutation), `false` when the form is
     * invalid or the save failed — the fields and `saveFailed` say which.
     */
    save: async (): Promise<boolean> => {
      // `trigger` validates through the schema and shows each field's error.
      if (!(await form.trigger())) return false;
      try {
        // The schema's output: the country upper-cased, the city trimmed.
        await complete.mutateAsync(citizenSetupSchema.parse(form.getValues()));
        return true;
      } catch {
        return false;
      }
    },
    isSaving: complete.isPending,
    saveFailed: complete.isError,
  };
}

export type ResidenceState = ReturnType<typeof useResidence>;
